import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import GlobalHeader from "../../../components/GlobalHeader";
import SnackAlert from "../../../components/SnackAlert";
import useApi from "../../../hooks/useApi";
import useSnack from "../../../hooks/useSnack";

// Pagos de Direct Links que no avanzaron.
//
// Un pedido pagado recién llega al difusor cuando quien pagó demuestra que
// el correo es suyo. Si lo escribió mal, o nunca abre el enlace, el dinero
// queda cobrado y el pedido no sale: esta pantalla los junta para
// resolverlos.

const MOTIVOS = {
  falta_verificar_correo: {
    label: "Falta verificar el correo",
    color: "warning",
    ayuda:
      "Nadie abrió el enlace de verificación. Si el correo está mal escrito, corrígelo; si está bien, reenvíalo.",
  },
  falta_iniciar_sesion: {
    label: "Falta iniciar sesión",
    color: "info",
    ayuda:
      "El correo ya tiene cuenta: su dueño tiene que iniciar sesión y vincular la solicitud.",
  },
  captura_sin_confirmar: {
    label: "Captura de PayPal sin confirmar",
    color: "error",
    ayuda:
      "PayPal pudo haber cobrado sin que Radar lo registrara. Revisa la orden en PayPal antes de hacer nada.",
  },
};

const HORAS = [
  { value: 24, label: "Más de 1 día" },
  { value: 48, label: "Más de 2 días" },
  { value: 72, label: "Más de 3 días" },
  { value: 168, label: "Más de 1 semana" },
];

const fecha = (iso) =>
  new Date(iso).toLocaleString("es", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });

const espera = (horas) =>
  horas < 48 ? `${horas} h` : `${Math.floor(horas / 24)} días`;

export default function DirectLinksAdmin() {
  const { loadApi, loadingApi } = useApi();
  const { msgSB, openSB, showSnack, typeSB } = useSnack();
  const [horas, setHoras] = useState(48);
  const [filas, setFilas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [correoTarget, setCorreoTarget] = useState(null);
  const [nuevoCorreo, setNuevoCorreo] = useState("");
  const [reembolsoTarget, setReembolsoTarget] = useState(null);
  const [nota, setNota] = useState("");

  const cargar = () => {
    setCargando(true);
    loadApi(`admin/direct-links/trabados?horas=${horas}`, true, "get")
      .then((res) => setFilas(res.data.filas || []))
      .catch((e) => showSnack(e.message, "error"))
      .finally(() => setCargando(false));
  };

  useEffect(() => {
    cargar();
  }, [horas]);

  const reenviar = (fila) =>
    loadApi(`admin/direct-links/${fila.id}/reenviar`, true, "post", {})
      .then((res) => {
        showSnack(res.data.message);
        cargar();
      })
      .catch((e) => showSnack(e.message, "error"));

  const corregirCorreo = () =>
    loadApi(`admin/direct-links/${correoTarget.id}/correo`, true, "put", {
      email: nuevoCorreo,
    })
      .then((res) => {
        showSnack(res.data.message);
        setCorreoTarget(null);
        cargar();
      })
      .catch((e) => showSnack(e.message, "error"));

  const reembolsar = () =>
    loadApi(`admin/direct-links/${reembolsoTarget.id}/reembolso`, true, "post", {
      nota,
    })
      .then((res) => {
        showSnack(res.data.message);
        setReembolsoTarget(null);
        cargar();
      })
      .catch((e) => showSnack(e.message, "error"));

  return (
    <>
      <GlobalHeader />
      <Container>
        <Stack gap={2} pb={6}>
          <Typography variant="h4">Direct Links: pagos sin avanzar</Typography>
          <Typography color="text.secondary">
            Solicitudes pagadas que todavía no llegaron al difusor, porque
            nadie verificó el correo o no se inició sesión para vincularlas.
          </Typography>

          <TextField
            select
            size="small"
            label="Esperando desde hace"
            value={horas}
            onChange={(e) => setHoras(Number(e.target.value))}
            sx={{ maxWidth: 240 }}
          >
            {HORAS.map((h) => (
              <MenuItem key={h.value} value={h.value}>
                {h.label}
              </MenuItem>
            ))}
          </TextField>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Pagado</TableCell>
                  <TableCell>Correo</TableCell>
                  <TableCell>Proyecto</TableCell>
                  <TableCell>Difusor · servicio</TableCell>
                  <TableCell>Pago</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cargando && filas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>Cargando…</TableCell>
                  </TableRow>
                )}
                {!cargando && filas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography color="text.secondary" py={2} textAlign="center">
                        No hay pagos trabados en este período.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {filas.map((fila) => {
                  const motivo = MOTIVOS[fila.motivo] || {};
                  return (
                    <TableRow key={fila.id}>
                      <TableCell>
                        {fecha(fila.desde)}
                        <Typography variant="caption" display="block" color="text.secondary">
                          hace {espera(fila.horasEsperando)}
                        </Typography>
                      </TableCell>
                      <TableCell>{fila.email}</TableCell>
                      <TableCell>
                        {fila.proyecto}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {fila.responsable}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {fila.difusor || "—"}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {fila.servicio}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {fila.metodo === "paypal" ? "PayPal" : "RadarCoins"} · {fila.monto}
                        {fila.payToken && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Orden {fila.payToken}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={motivo.label} color={motivo.color} />
                        <Typography variant="caption" display="block" color="text.secondary" mt={0.5}>
                          {fila.verificacionEnviada
                            ? "Se envió la verificación"
                            : fila.cuentaExiste
                              ? "El correo tiene cuenta"
                              : "No se llegó a enviar la verificación"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
                          {fila.motivo !== "captura_sin_confirmar" && (
                            <LoadingButton
                              size="small"
                              loading={loadingApi.includes(
                                `post__admin/direct-links/${fila.id}/reenviar`
                              )}
                              onClick={() => reenviar(fila)}
                            >
                              Reenviar correo
                            </LoadingButton>
                          )}
                          {fila.metodo === "paypal" &&
                            fila.motivo !== "captura_sin_confirmar" && (
                              <Button
                                size="small"
                                onClick={() => {
                                  setCorreoTarget(fila);
                                  setNuevoCorreo(fila.email);
                                }}
                              >
                                Corregir correo
                              </Button>
                            )}
                          <Button
                            size="small"
                            color="error"
                            onClick={() => {
                              setReembolsoTarget(fila);
                              setNota("");
                            }}
                          >
                            Reembolsar
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </Container>

      {/* Corregir el correo */}
      <Dialog open={Boolean(correoTarget)} onClose={() => setCorreoTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Corregir el correo de la solicitud</DialogTitle>
        <DialogContent>
          <Stack gap={2} pt={1}>
            <Alert severity="warning">
              El pedido pasa a quien controle el correo nuevo. Hazlo solo después
              de confirmar por fuera de Radar —por ejemplo con el comprobante de
              PayPal— que quien lo pide es quien pagó.
            </Alert>
            <Typography variant="body2">
              Correo actual: <strong>{correoTarget?.email}</strong>
            </Typography>
            <TextField
              label="Correo correcto"
              type="email"
              value={nuevoCorreo}
              onChange={(e) => setNuevoCorreo(e.target.value)}
              fullWidth
            />
            <Typography variant="caption" color="text.secondary">
              Se le envía el correo para verificarlo, o para iniciar sesión si
              ya tiene cuenta. La verificación del correo anterior deja de valer.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button onClick={() => setCorreoTarget(null)}>Cancelar</Button>
          <LoadingButton
            variant="contained"
            disabled={!nuevoCorreo || nuevoCorreo.trim().toLowerCase() === correoTarget?.email}
            loading={loadingApi.includes(`put__admin/direct-links/${correoTarget?.id}/correo`)}
            onClick={corregirCorreo}
          >
            Corregir y enviar
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Reembolsar */}
      <Dialog open={Boolean(reembolsoTarget)} onClose={() => setReembolsoTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle color="error">Reembolsar la solicitud</DialogTitle>
        <DialogContent>
          <Stack gap={2} pt={1}>
            {reembolsoTarget?.metodo === "paypal" ? (
              <Alert severity="error">
                Primero haz el reembolso en PayPal (orden{" "}
                <strong>{reembolsoTarget?.payToken}</strong>,{" "}
                {reembolsoTarget?.monto}). Este botón solo lo registra en Radar
                y cierra la solicitud: no mueve dinero.
              </Alert>
            ) : (
              <Alert severity="warning">
                Se devuelven {reembolsoTarget?.monto} a la cuenta de{" "}
                {reembolsoTarget?.email} y la solicitud se cierra.
              </Alert>
            )}
            {reembolsoTarget?.motivo === "captura_sin_confirmar" && (
              <Alert severity="info">
                Antes confirma en PayPal si la orden se cobró: si no se cobró,
                no hay nada que devolver.
              </Alert>
            )}
            <TextField
              label="Nota (opcional)"
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              multiline
              rows={2}
              fullWidth
            />
            <Box>
              <Typography variant="caption" color="text.secondary">
                Una solicitud reembolsada ya no se puede activar, vincular ni
                volver a cobrar.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3 }}>
          <Button onClick={() => setReembolsoTarget(null)}>Cancelar</Button>
          <LoadingButton
            color="error"
            variant="contained"
            loading={loadingApi.includes(`post__admin/direct-links/${reembolsoTarget?.id}/reembolso`)}
            onClick={reembolsar}
          >
            {reembolsoTarget?.metodo === "paypal"
              ? "Ya reembolsé en PayPal"
              : "Devolver los RC"}
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <SnackAlert openSB={openSB} typeSB={typeSB} msgSB={msgSB} />
    </>
  );
}
